import { css } from '@emotion/react';
import { novoEdBlack, novoEdBlue } from 'athena/styles/colors';
import { Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';
import { danger, gray2 } from 'styles/global_defaults/colors';
import ClickableContainer from 'components/clickable-container';
import { textLargeBodyFontSize, textLargeLineHeight } from 'styles/global_defaults/fonts';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';
import { tooltipZIndex } from '../constants';
import AthenaTextarea from '../textarea';

const DESCRIPTION_MAX_LINES = 3;

type GoalDescriptionProps = {
  isReadOnly: boolean;
  goalItemId: number;
  description: string;
  onChange: () => void;
  onReset: () => void;
  isEditing: boolean;
  setEditingId: (id: number | null) => void;
};

const textStyles = css`
  color: ${novoEdBlack} !important;
  font-size: ${textLargeBodyFontSize}px !important;
  line-height: ${textLargeLineHeight}px !important;
`;

const styles = css`
  margin: ${quarterSpacing / 2}px 0;
  &.add-goal-item-button {
    padding: 0;
    color: ${gray2};

    &:focus,
    &:active {
      box-shadow: none !important;
      border-radius: unset;
      outline-offset: 0;
    }
  }

  &.goal-item-description {
    display: grid;
    ${textStyles}
    .input-label {
      margin-bottom: 0 !important;
    }
    .input-content {
      ${textStyles}
    }
    textarea {
      display: flex;
      padding: 0 !important;
      min-height: ${textLargeLineHeight}px !important;
      border: none !important;
      border-radius: unset !important;
      outline: 0px solid transparent !important;
      border-bottom: 1px solid ${novoEdBlue} !important;

      ::placeholder {
        ${textStyles}
        color: ${gray2} !important;
      }
      &.error {
        border-bottom: 1px solid ${danger} !important;
      }
    }
    .truncated-text {
      height: unset;
      max-height: ${DESCRIPTION_MAX_LINES * textLargeLineHeight}px;
      border-bottom: 1px solid transparent !important;
    }
  }
`;

const GoalDescription = ({ isReadOnly, goalItemId, isEditing, description, setEditingId, onChange, onReset }: GoalDescriptionProps) => {
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GOALS.ITEM;
  const { setValue, formState: { errors } } = useFormContext();
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.goals.list.item;

  const clearDescription = () => {
    onReset();
    setEditingId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!errors.description) {
        onChange();
      }
    } else if (e.key === 'Escape') {
      clearDescription();
    }
  };

  const delayClearDescription = () => {
    setTimeout(() => {
      clearDescription();
    }, 500);
  };

  if (isEditing) {
    return (
      <AthenaTextarea
        withForm
        autoFocus
        css={styles}
        showLabel={false}
        name='description'
        value={description}
        onKeyDown={handleKeyDown}
        className='goal-item-description'
        inputClassName={`px-0 input-content ${errors.description ? 'error' : ''}`}
        placeholder={translationBase.ADD_BUTTON()}
        onChange={({ target: { value } }) => setValue('description', value)}
        onBlur={goalItemId && !errors.description ? onChange : delayClearDescription}
        validationErrorTitle={t.FORM.WARNING()}
        onFocus={({ target }) => target.setSelectionRange(target.value.length, target.value.length)}
        dataQa={dataQATag.editGoalDescriptionTextField}
        dataQaId={`${dataQATag.editGoalDescriptionTextField}-${goalItemId}`}
      />
    );
  }
  if (description) {
    return (
      <ClickableContainer
        css={styles}
        disabled={isReadOnly}
        className='goal-item-description'
        onClick={() => setEditingId(goalItemId)}
        data-qa={dataQATag.editGoalDescription}
        data-qa-id={`${dataQATag.editGoalDescription}-${goalItemId}`}
      >
        <NvTruncatedTextWithTooltip
          text={description}
          linesCount={DESCRIPTION_MAX_LINES}
          className='truncated-text d-flex align-items-start'
          zIndex={tooltipZIndex}
        />
      </ClickableContainer>
    );
  }
  return (
    <Button
      css={styles}
      variant='link'
      disabled={isReadOnly}
      onClick={() => setEditingId(goalItemId)}
      className='add-goal-item-button text-left text-large-body border-0'
      data-qa={dataQATag.createNewGoal}
    >
      {translationBase.ADD_BUTTON()}
    </Button>
  );
};

export default GoalDescription;
